import { genTopics } from "@/api/langgraph";
import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  ModalProps,
  Select,
  Tag,
} from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import TextArea from "antd/es/input/TextArea";
import { DefaultOptionType } from "antd/es/select";
import { MixMatchChoicesBody, Theme } from "api/config/chalice-types";
import InfoTooltip from "components/Common/InfoTooltip";
import SegmentedFormItem from "components/Common/SegmentedFormItem";
import {
  createFileInputProps,
  TaskInputForm,
  TaskInputProps,
} from "components/FileAndUrlInputTypes";
import ModalWithBorders from "components/ModalWithBorders";
import PaletteColorBlocks from "components/PaletteColorBlocks";
import useThemes from "hooks/useThemes";
import { useMemoriesQuery } from "pages/Memory/hooks/useMemoriesQuery";
import { useEffect, useMemo, useState } from "react";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import { AppDispatch, useAppDispatch, useAppSelector } from "store";
import { generatePostsThunk } from "store/posts/postActions";
import { currentBusinessGetter } from "store/user/userSlice";
import PRESET_PALETTES from "../PostEditor/DesignEditor/ColorPaletteSelect/PRESET_PALETTES";
import ThemeOptionLabel from "../components/ThemeOptionLabel";

export type GeneratePostsFrom = { type: "topic" | "memory"; id: string };

export type ComplexInputType = "text" | "pdf" | "url";

type GeneratePostForm = TaskInputForm & {
  generateWith: ComplexInputType | GeneratePostsFrom["type"];
  numberOfPosts: number;
  themes: string[];
  palettes: [];
  cta: string;
  text?: string;
  topic?: string;
  memory?: string;
};

const NewGeneratePanel = ({
  generateFrom,
  onSubmit,
  ...modalProps
}: {
  generateFrom?: GeneratePostsFrom;
  onSubmit: () => void;
} & ModalProps) => {
  const [form] = useForm<GeneratePostForm>();
  const dispatch: AppDispatch = useAppDispatch();

  const [isGenerating, setIsGenerating] = useState(false);

  const currentBusiness = useAppSelector(currentBusinessGetter);
  const { themes, isLoading: themesLoading } = useThemes();

  const [showMoreSettings, setShowMoreSettings] = useState(false);

  const generateWith = useWatch("generateWith", form);

  const { data: memories, isLoading: memoriesLoading } = useMemoriesQuery();

  useEffect(() => {
    if (generateFrom?.type) {
      const setValue = options[generateFrom.type]?.find(
        ({ value }) => value === generateFrom.id
      );
      setValue && form.setFieldValue(generateFrom.type, setValue.value);
    }
  }, [generateFrom, form]);

  // Effect to handle Cmd/Ctrl+Enter submission
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault(); // Prevent default Enter behavior (like adding newline in textarea)
        form.submit(); // Submit the form
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Cleanup listener on unmount
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [form]);

  const businessOptions = useMemo(
    () => ({
      topic: currentBusiness.topics.reduce((prev, { title, enabled }) => {
        enabled &&
          prev.push({
            label: title,
            value: title,
          });
        return prev;
      }, [] as DefaultOptionType[]),
      memory: memories?.map((memory) => ({
        label: memory.title,
        value: memory.id,
      })),
      palette: [
        ...(currentBusiness.brand.palettes || []),
        ...PRESET_PALETTES,
      ].map((palette, index) => ({
        label: <PaletteColorBlocks palette={palette} />,
        value: index,
        palette: palette,
      })),
    }),
    [memories, currentBusiness]
  );

  // Sort themes: business selected first, then alphabetical
  const sortedThemes = useMemo(() => {
    const businessThemeNames = currentBusiness.brand?.themes || [];
    // Ensure themes is an array before sorting
    const themesArray = Array.isArray(themes) ? themes : [];

    // Create a copy before sorting
    return [...themesArray].sort((a, b) => {
      const aName = a.name;
      const bName = b.name;

      const aIsSelected = businessThemeNames.includes(aName);
      const bIsSelected = businessThemeNames.includes(bName);

      if (aIsSelected && !bIsSelected) {
        return -1; // a comes first
      } else if (!aIsSelected && bIsSelected) {
        return 1; // b comes first
      } else {
        // If selection status is the same, sort alphabetically by display name or name
        return (a.display_name || aName).localeCompare(b.display_name || bName);
      }
    });
  }, [themes, currentBusiness.brand?.themes]);

  const themeOptions = useMemo(
    () =>
      // Use the sorted themes list
      sortedThemes.map((theme: Theme) => ({
        label: (
          <ThemeOptionLabel
            theme={theme}
            businessThemeNames={currentBusiness.brand?.themes || []}
          />
        ), // Render component
        value: theme.name,
        key: theme.display_name,
      })),
    [sortedThemes, currentBusiness.brand?.themes] // Depend on sorted list and business themes
  );

  const options = { ...businessOptions, themes: themeOptions };

  const generateWithInputFieldConfig: TaskInputProps<GeneratePostForm> =
    useMemo(() => {
      switch (generateWith) {
        case "topic":
          return {
            label: "Which topic do you want to use?",
            name: "topic",
            children: (
              <Select placeholder="Select a topic" options={options.topic} />
            ),
          };
        case "memory":
          return {
            input: <Select placeholder="Select a memory" />,
            label: "Which memory do you want to use?",
            name: "memory",
            children: (
              <Select
                loading={memoriesLoading}
                placeholder="Select a memory"
                options={options.memory}
              />
            ),
          };
        case "url":
          return {
            name: "url",
            children: (
              <Form.Item name="url" className="!m-0">
                <Input placeholder="Drop a link to your website, blog post, news article, etc." />
              </Form.Item>
            ),
            label: "What link to do want to use to generate posts?",
          };
        case "pdf":
          return createFileInputProps({
            label: "PDF File",
            accept: ".pdf",
          });
        default:
          return {
            label: "What do you want to post about?",
            name: "text",
            children: (
              <TextArea placeholder="Type in whatever you would like to post on." />
            ),
          };
      }
    }, [generateWith]);

  const getThemeName = (theme: string) =>
    themes.find(({ name }: Theme) => name === theme)?.display_name;

  const submitForm = async (values: GeneratePostForm) => {
    const body: MixMatchChoicesBody = {
      // If generateWith is 'text', pass the text as a topic. Otherwise, pass the selected topic if generateWith is 'topic'.
      topics:
        // Note: Check generateWith case-insensitively. At runtime, the form value might unexpectedly be uppercase (e.g., "TEXT")
        // even if the SegmentedFormItem options are lowercase ("text").
        values.generateWith.toLowerCase() === "text"
          ? values.text
            ? [values.text]
            : undefined
          : values.generateWith === "topic" && values.topic
            ? [values.topic]
            : undefined,
      // TODO: Handle 'memory' generation type. MixMatchChoicesBody does not have a 'memories' field.
      theme_names: values.themes?.length > 0 ? values.themes : undefined,
      ctas: values.cta ? [values.cta] : undefined,
      palettes:
        values.palettes?.length > 0
          ? values.palettes
              .map((index) => options.palette[index]?.palette)
              .filter(Boolean)
          : undefined,
      design_template_ids: undefined, // Not currently configurable in this form
      batch_size: values.numberOfPosts, // Map numberOfPosts to batch_size
      // TODO: Handle 'url' and 'pdf' generation types. They do not map directly to MixMatchChoicesBody.
    };

    try {
      setIsGenerating(true);
      // Pretty hacky way to generate posts from a url (via creating a topic first)
      // (idealy we shouldn't be reaching from pages -> api (should use a hook!))
      // But I want to make a new flow for generating posts from a url anyways so is temp
      if (values.generateWith.toLowerCase() === "url" && values.url) {
        message.info("Scanning website, this may take up to 30 seconds", 5);
        const result = await genTopics({
          business_id: currentBusiness.id,
          url: values.url,
        });
        body.topics = result?.topics.map((topic) => topic.body);
        message.info("Website successfully scanned! Now generating posts...");
      }
      await dispatch(generatePostsThunk(body));
      onSubmit();
    } catch (error) {
      console.error("Failed to generate posts:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <ModalWithBorders
      classNames={{ body: "max-h-[75vh] overflow-auto" }}
      title="Generate Posts"
      open
      okText="Generate"
      okButtonProps={{ loading: isGenerating }}
      onOk={form.submit}
      {...modalProps}
    >
      <Form
        onFinish={submitForm}
        id="generate-panel-form"
        initialValues={{
          generateWith: generateFrom?.type || "text",
          numberOfPosts: 5,
          theme: currentBusiness.brand?.themes || [],
          palettes: [],
          cta: null,
          addToMemory: true,
        }}
        layout="vertical"
        form={form}
      >
        <SegmentedFormItem
          options={["text", "topic", "url"]}
          formItemProps={{
            label: "Generate with",
            name: "generateWith",
          }}
        />
        <Form.Item {...generateWithInputFieldConfig} />
        <Form.Item
          label="Number of Posts"
          name="numberOfPosts"
          initialValue={5}
        >
          <InputNumber min={1} max={20} className="w-full" addonAfter="posts" />
        </Form.Item>
        <Button
          onClick={() => setShowMoreSettings(!showMoreSettings)}
          icon={
            showMoreSettings ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />
          }
          type="link"
        >
          {showMoreSettings ? "Hide" : "More"} Settings
        </Button>
        {showMoreSettings && (
          <>
            <Form.Item
              label={
                <div className="flex items-center">
                  <span>Design Themes</span>
                  <InfoTooltip title="If none is selected, all your brand themes will be shuffled." />{" "}
                </div>
              }
              name="theme"
            >
              <Select
                tagRender={({ value, closable, onClose }) => (
                  <Tag className="m-0.5" closable={closable} onClose={onClose}>
                    {getThemeName(value)}
                  </Tag>
                )}
                placeholder="All Themes"
                loading={themesLoading}
                options={options.themes}
                mode="tags"
              />
            </Form.Item>
            <Form.Item
              label={
                <div className="flex items-center">
                  <span>Color Palettes</span>
                  <InfoTooltip title="If none is selected, all brand color palettes will be shuffled." />{" "}
                </div>
              }
              name="palettes"
            >
              <Select
                options={options.palette}
                placeholder="All Palettes"
                mode="tags"
              />
            </Form.Item>
            <Form.Item
              label={
                <div className="flex items-center">
                  <span>Call To Action</span>
                  <InfoTooltip title="This field is for the message or prompt that encourages your audience to take a specific action, such as 'Buy Now', 'Sign Up,' or 'Learn More.' It should be clear, concise, and motivating." />
                </div>
              }
              name="cta"
            >
              <Select
                placeholder="All CTAs"
                options={currentBusiness.ctas.map((cta) => ({
                  label: cta,
                  value: cta,
                }))}
                allowClear
              />
            </Form.Item>
          </>
        )}
      </Form>
    </ModalWithBorders>
  );
};

export default NewGeneratePanel;
